/**
 * SpeedControl Component
 *
 * Dropdown menu for selecting playback speed.
 * Shows current speed and allows selection from predefined options.
 */

import { Button } from "@/components/ui/button";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { getPlayerActions } from "@/lib/player/player.store";
import { PLAYBACK_SPEED_OPTIONS } from "./player.constants";
import { useTranslation } from "react-i18next";

interface SpeedControlProps {
	currentSpeed: number;
}

/**
 * SpeedControl Component
 *
 * Dropdown menu for selecting playback speed.
 * Shows current speed and allows selection from predefined options.
 */
export function SpeedControl({ currentSpeed }: SpeedControlProps) {
	const { t } = useTranslation();

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					variant="ghost"
					size="icon"
					className="h-10 min-w-[3rem] px-2 text-white hover:text-primary hover:bg-white/10 hover:scale-105 active:scale-95 transition-all duration-150 rounded-full"
					aria-label={t("player.playbackSpeed")}
				>
					<span className="text-sm font-semibold">{currentSpeed}x</span>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuContent align="end" className="w-44 rounded-xl">
				<DropdownMenuLabel className="text-xs px-3 py-2">
					{t("player.playbackSpeed")}
				</DropdownMenuLabel>
				<DropdownMenuSeparator />
				{PLAYBACK_SPEED_OPTIONS.map((speed) => (
					<DropdownMenuItem
						key={speed}
						onClick={() => getPlayerActions().setPlaybackSpeed(speed)}
						className={`px-3 py-2.5 cursor-pointer ${currentSpeed === speed ? "bg-secondary" : ""}`}
					>
						<span className="font-medium">{speed}x</span>
						{speed === 1 && (
							<span className="ml-2 text-muted-foreground text-sm">
								({t("player.normal")})
							</span>
						)}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}
